import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Mail, Clock, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { API_URL } from '@/services/apiClient';

interface Invite {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string | null;
  createdAt: string;
  daysUntilExpiry: number | null;
}

interface InviteStats {
  total: number;
  pending: number;
  accepted: number;
  expired: number;
}

interface InviteData {
  invites: Invite[];
  stats: InviteStats;
}

const STATUS_STYLES: Record<string, { light: string; dark: string; label: string }> = {
  pending: { light: 'bg-amber-100 text-amber-700', dark: 'bg-amber-900/40 text-amber-400', label: 'Pending' },
  accepted: { light: 'bg-emerald-100 text-emerald-700', dark: 'bg-emerald-900/40 text-emerald-400', label: 'Accepted' },
  expired: { light: 'bg-red-100 text-red-700', dark: 'bg-red-900/40 text-red-400', label: 'Expired' },
};

function formatDate(str: string) {
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function InviteTracker({ dm }: { dm: boolean }) {
  const [data, setData] = useState<InviteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'accepted' | 'expired'>('all');
  const [resendingId, setResendingId] = useState<string | null>(null);

  const cardCls = `border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls = dm ? 'text-white' : 'text-slate-800';
  const descCls = dm ? 'text-white' : 'text-slate-500';
  const bodyTextCls = dm ? 'text-white' : 'text-slate-700';
  const mutedCls = dm ? 'text-white' : 'text-slate-400';
  const borderCls = dm ? 'border-slate-700' : 'border-slate-200';
  const rowHover = dm ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50';

  const fetch_ = useCallback(async () => {
    const token = sessionStorage.getItem('authToken');
    try {
      const res = await fetch(`${API_URL}/dashboard/school-admin/invites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch {/* */} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  const handleResend = async (userId: string) => {
    setResendingId(userId);
    const token = sessionStorage.getItem('authToken');
    try {
      await fetch(`${API_URL}/dashboard/school-admin/users/${userId}/resend-invite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {/* */} finally { setResendingId(null); }
  };

  const filtered = (data?.invites || []).filter(i => filter === 'all' || i.status === filter);

  const acceptanceRate = data?.stats.total
    ? Math.round((data.stats.accepted / data.stats.total) * 100)
    : 0;

  return (
    <div className="space-y-5" data-dm={dm}>
      <h2 className={`text-lg font-semibold ${titleCls}`}>User Onboarding & Invite Tracker</h2>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Invites', value: data?.stats.total ?? 0, icon: Mail, color: 'blue' },
          { label: 'Pending', value: data?.stats.pending ?? 0, icon: Clock, color: 'amber' },
          { label: 'Accepted', value: data?.stats.accepted ?? 0, icon: CheckCircle2, color: 'emerald' },
          { label: 'Expired', value: data?.stats.expired ?? 0, icon: XCircle, color: 'red' },
        ].map((s, i) => (
          <Card key={i} className={cardCls}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2.5">
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${dm ? `bg-${s.color}-900/50` : `bg-${s.color}-100`}`}>
                  <s.icon className={`h-4 w-4 text-${s.color}-500`} />
                </div>
                <div>
                  <p className={`text-xs ${descCls}`}>{s.label}</p>
                  <p className={`text-xl font-bold ${titleCls}`}>{s.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Acceptance rate bar */}
      {(data?.stats.total ?? 0) > 0 && (
        <Card className={cardCls}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-2">
              <p className={`text-sm font-medium ${bodyTextCls}`}>Invitation Acceptance Rate</p>
              <span className={`text-sm font-bold ${acceptanceRate >= 70 ? 'text-emerald-500' : acceptanceRate >= 40 ? 'text-amber-500' : 'text-red-500'}`}>
                {acceptanceRate}%
              </span>
            </div>
            <div className={`h-2.5 rounded-full overflow-hidden ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
              <div className="h-full bg-emerald-500 rounded-full transition-all duration-700"
                style={{ width: `${acceptanceRate}%` }} />
            </div>
            <p className={`text-xs mt-1.5 ${mutedCls}`}>{data?.stats.accepted} of {data?.stats.total} invites accepted</p>
          </CardContent>
        </Card>
      )}

      {/* Filter + table */}
      <Card className={cardCls}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className={`text-base ${titleCls}`}>Invitations</CardTitle>
              <CardDescription className={descCls}>{filtered.length} shown</CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              {(['all', 'pending', 'accepted', 'expired'] as const).map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`btn-glass btn-glass-pill text-xs capitalize ${filter === f ? 'btn-glass-blue' : ''}`}>{f}</button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className={`flex flex-col items-center justify-center h-32 ${mutedCls}`}>
              <Mail className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-sm">No invitations found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b text-xs font-medium uppercase tracking-wider ${borderCls} ${descCls}`}>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">Role</th>
                    <th className="px-4 py-3 text-center">Status</th>
                    <th className="px-4 py-3 text-center">Expires In</th>
                    <th className="px-4 py-3 text-left">Sent</th>
                    <th className="px-4 py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${dm ? 'divide-slate-700' : 'divide-slate-100'}`}>
                  {filtered.map(invite => {
                    const s = STATUS_STYLES[invite.status] || STATUS_STYLES.pending;
                    const expiringSoon = invite.status === 'pending' && invite.daysUntilExpiry !== null && invite.daysUntilExpiry <= 2;
                    return (
                      <tr key={invite.id} className={`transition-colors ${rowHover}`}>
                        <td className="px-4 py-3">
                          <p className={`font-medium truncate max-w-[200px] ${bodyTextCls}`}>{invite.email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`capitalize text-xs ${mutedCls}`}>{invite.role}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${dm ? s.dark : s.light}`}>
                            {s.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {invite.status === 'pending' && invite.daysUntilExpiry !== null ? (
                            <span className={`text-xs font-medium ${expiringSoon ? 'text-red-500' : mutedCls}`}>
                              {invite.daysUntilExpiry === 0 ? 'Today' : `${invite.daysUntilExpiry}d`}
                            </span>
                          ) : (
                            <span className={`text-xs ${mutedCls}`}>—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs ${mutedCls}`}>{formatDate(invite.createdAt)}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {(invite.status === 'pending' || invite.status === 'expired') && (
                            <button
                              onClick={() => handleResend(invite.id)}
                              disabled={resendingId === invite.id}
                              className="btn-glass btn-glass-pill flex items-center gap-1 mx-auto text-xs"
                            >
                              <RefreshCw className={`h-3 w-3 ${resendingId === invite.id ? 'animate-spin' : ''}`} />
                              Resend
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
